import React, { useState, lazy, Suspense } from 'react';
import { OrdersPage } from './pages/OrdersPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { CalendarPage } from './pages/CalendarPage';
import { TemplatesPage } from './pages/TemplatesPage';
import type { DietPanelTab, DietRole } from './types/dietitian.types';
import './dietitian-panel.css';

// Lazy-load адмін-сторінок (завантажуються тільки для адміна)
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage').then(m => ({ default: m.AdminOrdersPage })));
const AdminGrafikPage = lazy(() => import('./pages/AdminGrafikPage').then(m => ({ default: m.AdminGrafikPage })));

const Icons = {
  Clipboard: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>,
  Calendar:  () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
  FileText:  () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>,
  Grid:      () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>,
};

export const DietitianApp: React.FC = () => {
  const wpRole = (window as any).dietitianVars?.userRole || 'dietitian';
  const isAdmin = wpRole === 'administrator' || wpRole === 'admin';

  const [viewAs, setViewAs] = useState<DietRole>(isAdmin ? 'administrator' : 'dietitian');
  const [tab, setTab] = useState<DietPanelTab>(isAdmin ? 'admin-orders' : 'orders');
  const [activeOrderId, setActiveOrderId] = useState<number | null>(null);

  const user       = (window as any).dietitianVars?.initialUser || (window as any).cabinetVars?.initialUser;
  const userName   = user?.first_name
    ? user.first_name + ' ' + (user.last_name ? user.last_name.charAt(0) + '.' : '')
    : (user?.display_name || 'Dietetyk');
  const cabinetUrl = (window as any).dietitianVars?.cabinetUrl || (window as any).cabinetVars?.cabinetUrl || '/moje-konto/';

  const handleRoleSwitch = (role: DietRole) => {
    setViewAs(role);
    setActiveOrderId(null);
    setTab(role === 'administrator' ? 'admin-orders' : 'orders');
  };

  const openOrder = (id: number) => { setActiveOrderId(id); setTab('order-detail'); };
  const goBack    = () => { setActiveOrderId(null); setTab(viewAs === 'administrator' ? 'admin-orders' : 'orders'); };

  const renderContent = () => {
    if (viewAs === 'administrator') {
      switch (tab) {
        case 'admin-orders':
          return (
            <Suspense fallback={<div style={{ padding: 40, color: 'var(--v600)' }}>Ładowanie...</div>}>
              <AdminOrdersPage onSelectOrder={openOrder} />
            </Suspense>
          );
        case 'order-detail':
          if (!activeOrderId) return null;
          return <OrderDetailPage orderId={activeOrderId} onBack={goBack} isAdmin={true} />;
        case 'admin-grafik':
          return (
            <Suspense fallback={<div style={{ padding: 40, color: 'var(--v600)' }}>Ładowanie kalendarza...</div>}>
              <AdminGrafikPage onOpenOrder={openOrder} />
            </Suspense>
          );
        case 'templates':
          return <TemplatesPage />;
        default:
          return null;
      }
    }

    switch (tab) {
      case 'orders':
        return <OrdersPage onSelectOrder={openOrder} />;
      case 'order-detail':
        if (!activeOrderId) return <OrdersPage onSelectOrder={openOrder} />;
        return <OrderDetailPage orderId={activeOrderId} onBack={goBack} isAdmin={false} />;
      case 'calendar':
        return <CalendarPage onOpenOrder={openOrder} isEditable={false} />;
      case 'templates':
        return <TemplatesPage />;
      default:
        return null;
    }
  };

  return (
    <div className="react-cabinet-isolated diet-app-wrapper">
      <div className="app">
        <div className="side">

          {isAdmin && (
            <div className="roleswitch">
              <button className={viewAs === 'dietitian' ? 'on' : ''} onClick={() => handleRoleSwitch('dietitian')}>
                👩‍⚕️ Joanna
              </button>
              <button className={viewAs === 'administrator' ? 'on' : ''} onClick={() => handleRoleSwitch('administrator')}>
                🛡️ Admin
              </button>
            </div>
          )}

          {viewAs === 'administrator' ? (
            <>
              <div className="nav-section-label">Panel admina</div>
              <div className={`ni ${tab === 'admin-orders' ? 'on' : ''}`} onClick={() => { setTab('admin-orders'); setActiveOrderId(null); }}>
                <Icons.Grid /> <span>Wszystkie zamówienia</span>
              </div>
              <div className={`ni ${tab === 'admin-grafik' ? 'on' : ''}`} onClick={() => setTab('admin-grafik')}>
                <Icons.Calendar /> <span>Grafik dietetyka</span>
              </div>
              <div className={`ni ${tab === 'templates' ? 'on' : ''}`} onClick={() => setTab('templates')}>
                <Icons.FileText /> <span>Szablony zaleceń</span>
              </div>
            </>
          ) : (
            <>
              <div className="nav-section-label">Mój panel</div>
              <div className={`ni ${tab === 'orders' || tab === 'order-detail' ? 'on' : ''}`} onClick={() => { setTab('orders'); setActiveOrderId(null); }}>
                <Icons.Clipboard /> <span>Zlecenia</span>
              </div>
              <div className={`ni ${tab === 'calendar' ? 'on' : ''}`} onClick={() => setTab('calendar')}>
                <Icons.Calendar /> <span>Mój kalendarz</span>
              </div>
              <div className={`ni ${tab === 'templates' ? 'on' : ''}`} onClick={() => setTab('templates')}>
                <Icons.FileText /> <span>Szablony zaleceń</span>
              </div>
            </>
          )}

          <div className="prof" style={{ cursor: 'pointer', marginTop: 'auto' }} onClick={() => window.location.href = cabinetUrl}>
            <div className="av">{viewAs === 'administrator' ? '🛡️' : '👩‍⚕️'}</div>
            <div>
              <div className="nm">{userName}</div>
              <div className="rl">{viewAs === 'administrator' ? 'Administrator' : 'Dietetyk kliniczny'}</div>
            </div>
          </div>
        </div>

        <div className="main">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
